import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import Canvas from './Canvas';
import raw from 'raw.macro';
import copy from 'copy-to-clipboard';
import { KeyframeIndex, keyframeSetContainsFrame, ParsedShaderSet, PropertyKeyframeSet, PropIndex, ShaderId } from './parser/parser';
import { GLSLUniformLocations, Property, PropertyValue, Vec2 } from './parser/propTypes';
import PropsSidebar from './PropsSidebar';
import Header from './Header';
import { loadFractals, saveFractals } from './storage';
import Timeline, { editKeyframeInSet, insertKeyframeInSet, removeKeyframeInSet } from './Timeline';
import Render from './Render';

// Default fragment shader to display before a pipeline has been selected
const defaultFragShader = raw("../shaders/preamble.glsl") + raw("../shaders/default.glsl");

// A shader source file
export interface ShaderSource {
  src: string;
  filename: string;
}

export interface EditorProps {
  sources: ShaderSource[];
  // serialized fractal to display
  fractalProps: any;
  // if animation controls should be enabled by default
  // when editor is closed
  onClose: () => void;
  // display a application popup
  onPopupWindow: (children: ReactNode) => void;
}

// The root editor that shows shader output + allows changing pipeline + props
export default function Editor(props: EditorProps) {
  // Parsed shader sources
  const [parsedShaders, setParsedShaders] = useState<ParsedShaderSet>(new ParsedShaderSet([]));
  // Property values for each shader and animation keyframes for each
  const [shaderProps, setShaderProps] = useState<PropertyKeyframeSet>({default: [], keyframes: []});
  // Current time in animation
  const [time, setTime] = useState(0.0);
  // If animation is enabled
  const [doAnimate, setDoAnimate] = useState(false);
  // Currently selected keyframe
  const [activeKeyframe, setActiveKeyframe] = useState<KeyframeIndex | null>(null);

  // Active shaders -- selected shaders to be included in compilation
  const [activeShaders, setActiveShaders] = useState<ShaderId[]>([]);
  // Properties in activeShaders that want to receive canvas events
  const canvasWatchingProps = useRef<[ShaderId, PropIndex, Property][]>([]);
  // Drawing canvas
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
  // Current WebGL context
  const [gl, setGl] = useState<WebGL2RenderingContext | null>(null);
  // Current glsl fragment shader being displayed + the uniforms to extract locations for it from
  // This is the glsl code produced by combining all of the parsed shaders together
  const [fragGLSL, setFragGLSL] = useState<[string, string[]]>([defaultFragShader, []]);
  // Uniform locations in the glsl code
  const [uniformLocs, setUniformLocs] = useState<GLSLUniformLocations>({});
  // If the interactive editor is open or rendering is enabled
  const [rendering, setRendering] = useState(false);

  const { sources, fractalProps, onPopupWindow } = props;

  // Load + parse shaders from sources
  useEffect(() => {
    const [parsed, props] = ParsedShaderSet.fromSource(sources.map((src) => src.src));
    const errors = parsed.getErrors();
    if(errors.length !== 0) {
      // display errors in popup window
      onPopupWindow(
        <div>
          <h2>Shader Parse Error</h2>
          {errors.map((err) => 
            <div className="errorMsg">
              {sources[err.sourceId].filename}:{err.lineNum}: {err.msg}
            </div>
          )}
        </div>
      );
      return;
    }
    setParsedShaders(parsed);

    let finalProps = props;
    let finalSelected = [0];

    // check for a serialized state in url hash
    if(fractalProps !== null) {      
      const [newProps, selected] = parsed.deserializeKeyframeSet(fractalProps, sources.map((src) => src.filename), props);
      // if keyframes are present in state, enable animation by default
      if(keyframeSetContainsFrame(newProps)) {
        setDoAnimate(true);
      }
      finalProps = newProps;
      finalSelected = selected;
    }

    setActiveShaders(finalSelected);
    setShaderProps(finalProps);
  }, [sources]);

  // Compile glsl fragment shader
  const compile = useCallback((newShaderProps: PropertyValue[][]) => {
    const unmetDeps = parsedShaders.getUnmetDepends(activeShaders, 0);
    // if there are unmet dependencies or if no shaders are selected, don't do anything
    if(unmetDeps.length > 0 || activeShaders.length === 0) {
      // TODO: display unmet dependency errors
      setFragGLSL([defaultFragShader, []]);
    } else {
      const glsl = parsedShaders.emitGLSL(activeShaders, newShaderProps);
      const vars = parsedShaders.getUniformNames(activeShaders, newShaderProps);
      console.log(glsl);
      setFragGLSL([glsl, vars]);
    }
  }, [parsedShaders, activeShaders]);

  // When the selected shaders change, recompile glsl code
  useEffect(() => { 
      compile(parsedShaders.interpolate(shaderProps, time)); 
      // Select properties that want to receive canvas events
      let canvasListeners: [ShaderId, number, Property][] = [];
      for(let s = 0; s < activeShaders.length; s++) {
        const shaderId = activeShaders[s];
        const shader = parsedShaders.shaders[shaderId];
        for(let p = 0; p < shader.properties.length; p++) {
          const prop = shader.properties[p];
          if(prop.usesCanvasEvents())
            canvasListeners.push([shaderId, p, parsedShaders.shaders[shaderId].properties[p]]);
        }
      }
      canvasWatchingProps.current = canvasListeners;
    },
    // shaderProps is not a dependency -- we handle changes to props in the useEffect below
    [compile, parsedShaders, activeShaders]
  );

  // When props change, set uniforms
  useEffect(() => {
      if(gl === null) return;
      parsedShaders.setGLSLUniforms(activeShaders, parsedShaders.interpolate(shaderProps, time), gl, uniformLocs);
    },
    [uniformLocs, parsedShaders, time, shaderProps]
  );

  // Set the property at propIndex in shade to val
  const setShaderDefaultProp = useCallback((shader: ShaderId, propIndex: number, val: PropertyValue) => {
    if(gl === null) return;
    // set shaderProps to include the change
    const newProps = {
      ...shaderProps,
      default: {
        ...shaderProps.default,
        [shader]: {
          ...shaderProps.default[shader],
          [propIndex]: val
        }
      },
    };
    // Recompile the shader if needed
    const recompile = parsedShaders.propChangeRequiresGLSLCompile(shader, propIndex, shaderProps.default[shader][propIndex], val);
    if(recompile) {
      compile(parsedShaders.interpolate(newProps, time));
    }
    setShaderProps(newProps);
  }, [parsedShaders, shaderProps, gl, compile, time]);

  const setShaderProp = useCallback((shaderId: ShaderId, propIndex: number, val: PropertyValue) => {
    if(gl === null) return;
    const hasKeyframes = shaderProps.keyframes[shaderId][propIndex].length > 0;
    const isActiveKeyframe = hasKeyframes && activeKeyframe !== null && activeKeyframe[0] === shaderId && activeKeyframe[1] === propIndex;

    if(!hasKeyframes) {
      setShaderDefaultProp(shaderId, propIndex, val);
    } else if(isActiveKeyframe) {
      setShaderProps(editKeyframeInSet(shaderProps, activeKeyframe!, val.value));
    }
  }, [activeKeyframe, gl, shaderProps, setShaderDefaultProp]);

  // Callbacks to pass canvas events to properties that are interested in them
  function makeCanvasCallback<EventType>(callbackName: string) {
    return (event: EventType, mousePos: Vec2, aspectRatio: number, dist: number) => {
      canvasWatchingProps.current.forEach(([shader, propId, prop]) => {
        setShaderProp(shader, propId, (prop as any)[callbackName](event, parsedShaders.interpolateValue(shaderProps, shader, propId, time), mousePos, aspectRatio, dist));
      });
    };
  }

  const onWheel = useCallback(makeCanvasCallback<React.WheelEvent>("handleScroll"), [shaderProps, setShaderProp]);
  const onKeyDown = useCallback(makeCanvasCallback<React.KeyboardEvent>("handleKeyPress"), [shaderProps, setShaderProp]);
  const onMouseDrag = useCallback(makeCanvasCallback<React.MouseEvent>("handleMouseDrag"), [shaderProps, setShaderProp]);

  const curShaderProps = parsedShaders.interpolate(shaderProps, time);

  const setActiveKeyframeAndTime = useCallback((index: KeyframeIndex | null) => {
    setActiveKeyframe(index);
    if(index !== null) {
      setTime(shaderProps.keyframes[index[0]][index[1]][index[2]].time);
    }
  }, [shaderProps]);

  if(!rendering) {
    return (
      <div className="flex">
        <div className="headerCanvasContainer">
          <Header
            onUrlGen={() => {
              // generate save url: fractal props -> JSON -> base64 encode -> hash in url
              const data = parsedShaders.serializeKeyframeSet(sources.map((src) => src.filename), activeShaders, shaderProps);
              const base64Data = btoa(JSON.stringify(data));
              const url = `${window.location.host}/#${base64Data}`;
              onPopupWindow(
                <div className="fractalLink">
                  This fractal can be shared with the following URL: <button onClick={() => copy(url)}>Copy to Clipboard</button>
                  <a href={url}>{url}</a>
                </div>
              );
            }}
            onSave={() => {
              if(canvas !== null) {
                const name = prompt("Name");
                if(name === null) return;

                const data = parsedShaders.serializeKeyframeSet(sources.map((src) => src.filename), activeShaders, shaderProps);
                const imageURL = canvas.toDataURL("image/jpeg", 0.3);
                // append fractal props + image to localStorage saved_fractals entry
                const fractals = loadFractals();
                fractals.push({ name: name, data: data, image: imageURL });
                saveFractals(fractals);
                props.onClose();
              }
            }}
            onRender={() => setRendering(true)}
            onClose={props.onClose}
          ></Header>
          <Canvas
            fragShaderSrc={fragGLSL[0]}
            uniforms={fragGLSL[1]}
            uniformLocs={uniformLocs}
            setUniformLocs={setUniformLocs}
            gl={gl}
            setGLContext={setGl}
            setCanvas={setCanvas}
            props={curShaderProps}
            onKeyDown={onKeyDown}
            onWheel={onWheel}
            onMouseDrag={onMouseDrag}
            doAnimate={doAnimate}
          ></Canvas>
          {doAnimate &&
            <Timeline
              maxTime={10}
              parsedShaders={parsedShaders}
              activeShaders={activeShaders}
              keyframes={shaderProps}
              setKeyframes={setShaderProps}
              activeKeyframe={activeKeyframe}
              setActiveKeyframe={setActiveKeyframeAndTime}
              time={time}
              setTime={(time) => {
                setTime(time);
                setActiveKeyframe(null);
              }}
            ></Timeline>
          }
        </div>
        <div className="propSidebar">
          <PropsSidebar
            activeShaders={activeShaders}
            parsedShaders={parsedShaders}
            setActiveShaders={setActiveShaders}
            setShaderProp={setShaderProp}
            shaderProps={curShaderProps}
            shaderPropKeyframes={shaderProps}
            activeKeyframe={activeKeyframe}
            addKeyframe={([shaderId, propIndex]) => {
              const newVal = parsedShaders.interpolateValue(shaderProps, shaderId, propIndex, time).value;
              const [newProps, newActiveKeyframe] = insertKeyframeInSet(shaderProps, shaderId, propIndex, time, JSON.parse(JSON.stringify(newVal)));
              setShaderProps(newProps);
              setActiveKeyframe(newActiveKeyframe);
            }}
            deleteActiveKeyframe={() => {
              if(activeKeyframe !== null) {
                setShaderProps(removeKeyframeInSet(shaderProps, activeKeyframe));
                setActiveKeyframe(null);
              }
            }}
            onPopupWindow={onPopupWindow}
            doAnimate={doAnimate}
            setDoAnimate={setDoAnimate}
          ></PropsSidebar>
        </div>
      </div>
    );
  } else {
    return (
      <Render
        activeShaders={activeShaders}
        parsedShaders={parsedShaders}
        shaderProps={shaderProps}
        maxTime={10}
        onClose={props.onClose}
        onExitRender={() => setRendering(false)}
      />
    );
  }
}
